import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type FrequencyCapResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Frequency capping (G-172): how many marketing messages one person may get in
 * a period, across every campaign and journey. The send gate enforces it.
 */

const INPUT = 'h-input w-20 rounded-control border border-control bg-surface px-2 text-body text-ink';

export function FrequencyCapPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-065').enabled && can('campaign.send');
  const cap = useQuery<FrequencyCapResult, ApiError>({ queryKey: ['frequency-cap'], queryFn: () => api.frequencyCap({}), enabled });
  const [draft, setDraft] = useState({ active: false, max: '3', days: '7' });
  useEffect(() => {
    if (!cap.data) return;
    setDraft({ active: cap.data.active, max: String(cap.data.maxMessages ?? 3), days: String(cap.data.perDays ?? 7) });
  }, [cap.data]);
  const save = useMutation({
    mutationFn: () => api.saveFrequencyCap({ body: { active: draft.active, maxMessages: Number(draft.max), perDays: Number(draft.days) } }),
    onSuccess: (result) => queryClient.setQueryData(['frequency-cap'], result),
  });
  if (!enabled || !cap.data) return null;
  return (
    <Panel data-region="frequency-cap">
      <form className="flex flex-col gap-2" data-frequency-cap
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}>
        <SectionLabel>{t('frequencyCap.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('frequencyCap.help')}</p>
        <div className="flex flex-wrap items-center gap-2 text-dense text-ink">
          <label className="flex items-center gap-2">
            <input type="checkbox" data-cap-active checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
            {t('frequencyCap.atMost')}
          </label>
          <input className={INPUT} type="number" min={1} max={100} data-cap-max aria-label={t('frequencyCap.messages')} value={draft.max}
            onChange={(event) => setDraft({ ...draft, max: event.target.value })} />
          <span>{t('frequencyCap.every')}</span>
          <input className={INPUT} type="number" min={1} max={90} data-cap-days aria-label={t('frequencyCap.days')} value={draft.days}
            onChange={(event) => setDraft({ ...draft, days: event.target.value })} />
          <span>{t('frequencyCap.daysSuffix')}</span>
          <Button size="small" type="submit" data-cap-save disabled={save.isPending}>{t('frequencyCap.save')}</Button>
        </div>
        {save.error ? <p className="text-dense text-danger-text">{errorText(save.error as ApiError)}</p> : null}
      </form>
    </Panel>
  );
}
